import React, { useEffect, useMemo, useState } from "react";
import { Editor as TinyEditor } from "@tinymce/tinymce-react";
import { Icon } from "@iconify/react";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
} from "@mui/material";
import DOMPurify from "dompurify";
import { toast } from "react-toastify";
import {
  Box,
  ButtonPrimary,
  ButtonSecondary,
  HStack,
  VStack,
} from "../../components/utils";
import BCHeader from "../../shared/BCHeader";
import { SwitchButton } from "../../components/shared/SwitchButton";
import { InputWithLabel } from "../../components/shared/InputField";
import { FileDropzone } from "../../components/shared/FileDropzone";
import { Instructions } from "../../components/shared/Instructions";
import { FaqDocxParseResult, parseFaqDocx } from "../../api/faq";
import { useGetFaqSettings, useSaveFaqSettings } from "../../queries/faq";
import { Faq, FaqSettingInterface } from "../../types/faqPara";
import { determineUserAccessToResource } from "../../utils/auth";
import { Action, Resource } from "../../utils/interfaces/Companies";
import { usePermissionStore } from "../../store/permissionStore";

const EMPTY_FAQ: Faq = { question: "", answer: "" };

const normalizeQuestion = (question: string) =>
  question
    .toLowerCase()
    .replace(/what['’]s/g, "what is")
    .replace(/[^a-z0-9\s]/g, " ")
    .replace(/\s+/g, " ")
    .trim();

const questionSimilarity = (first: string, second: string) => {
  const ignoredWords = new Set([
    "a",
    "an",
    "the",
    "i",
    "my",
    "do",
    "can",
    "how",
  ]);
  const firstWords = new Set(
    normalizeQuestion(first)
      .split(" ")
      .filter((word) => word && !ignoredWords.has(word))
  );
  const secondWords = new Set(
    normalizeQuestion(second)
      .split(" ")
      .filter((word) => word && !ignoredWords.has(word))
  );
  const sharedWords = [...firstWords].filter((word) => secondWords.has(word));
  const allWords = new Set([...firstWords, ...secondWords]);
  return allWords.size === 0 ? 0 : sharedWords.length / allWords.size;
};

const escapeHtml = (value: string) =>
  value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");

async function parseFaqPdf(file: File): Promise<FaqDocxParseResult> {
  const { pdfjs } = await import("react-pdf");
  pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.js`;
  const pdf = await pdfjs.getDocument({
    data: new Uint8Array(await file.arrayBuffer()),
  }).promise;
  const blocks: FaqDocxParseResult["blocks"] = [];

  const pageBlocks = await Promise.all(
    Array.from({ length: pdf.numPages }, async (_, pageIndex) => {
      const pageNumber = pageIndex + 1;
      const page = await pdf.getPage(pageNumber);
      const content = await page.getTextContent();
      const lines: { y: number; text: string }[] = [];

      content.items.forEach((item: any) => {
        if (!item.str?.trim() || !item.transform) return;
        const y = Math.round(item.transform[5]);
        const existingLine = lines.find((line) => Math.abs(line.y - y) <= 3);
        if (existingLine)
          existingLine.text = `${existingLine.text} ${item.str}`;
        else lines.push({ y, text: item.str });
      });

      return lines
        .sort((first, second) => second.y - first.y)
        .map((line, lineIndex) => {
          const text = line.text.replace(/\s+/g, " ").trim();
          return {
            id: `page-${pageNumber}-block-${lineIndex + 1}`,
            html: `<p>${escapeHtml(text)}</p>`,
            text,
            suggestedQuestion: /\?$/.test(text),
          };
        })
        .filter((block) => Boolean(block.text));
    })
  );
  pageBlocks.forEach((page) => blocks.push(...page));

  if (blocks.length === 0) {
    throw new Error(
      "No selectable text was found. Scanned PDFs are not supported; upload a DOCX file instead."
    );
  }
  return { blocks };
}

const YOUTUBE_EMBED_HOSTS = new Set([
  "www.youtube.com",
  "youtube.com",
  "www.youtube-nocookie.com",
]);

const isHttpUrl = (value: string) => {
  try {
    const url = new URL(value);
    return url.protocol === "https:" || url.protocol === "http:";
  } catch {
    return false;
  }
};

const isYouTubeEmbedUrl = (value: string) => {
  try {
    const url = new URL(value);
    return (
      url.protocol === "https:" &&
      YOUTUBE_EMBED_HOSTS.has(url.hostname.toLowerCase()) &&
      url.pathname.startsWith("/embed/")
    );
  } catch {
    return false;
  }
};

const sanitizeFaqHtml = (html: string) => {
  const sanitizedHtml = DOMPurify.sanitize(html, {
    ALLOWED_TAGS: [
      "p",
      "br",
      "strong",
      "b",
      "em",
      "i",
      "u",
      "s",
      "ul",
      "ol",
      "li",
      "a",
      "table",
      "thead",
      "tbody",
      "tr",
      "th",
      "td",
      "h1",
      "h2",
      "h3",
      "h4",
      "blockquote",
      "span",
      "img",
      "iframe",
    ],
    // Keep imported/editor content to presentation-only HTML. Inline images
    // and YouTube embeds are checked again below against their source URLs.
    ALLOWED_ATTR: [
      "href",
      "target",
      "rel",
      "colspan",
      "rowspan",
      "src",
      "alt",
      "width",
      "height",
      "loading",
      "title",
      "allow",
      "allowfullscreen",
      "frameborder",
      "referrerpolicy",
    ],
    FORBID_TAGS: [
      "base",
      "embed",
      "form",
      "input",
      "math",
      "meta",
      "object",
      "script",
      "style",
      "svg",
    ],
    ALLOWED_URI_REGEXP: /^(?:(?:https?|mailto):|\/(?!\/)|#)/i,
    ALLOW_UNKNOWN_PROTOCOLS: false,
  });

  const container = document.createElement("div");
  container.innerHTML = sanitizedHtml;
  container.querySelectorAll("a").forEach((link) => {
    // Keep new-tab links from exposing the employee portal tab to the
    // destination page. Other target values are not needed for FAQ content.
    if (link.getAttribute("target") === "_blank") {
      link.setAttribute("rel", "noopener noreferrer");
    } else {
      link.removeAttribute("target");
      link.removeAttribute("rel");
    }
  });
  container.querySelectorAll("img").forEach((image) => {
    if (!isHttpUrl(image.getAttribute("src") || "")) image.remove();
  });
  container.querySelectorAll("iframe").forEach((frame) => {
    if (!isYouTubeEmbedUrl(frame.getAttribute("src") || "")) frame.remove();
  });
  return container.innerHTML;
};

const sanitizeFaq = (faq: Faq): Faq => ({
  question: faq.question.trim(),
  answer: sanitizeFaqHtml(faq.answer).trim(),
});

const FaqComponent = () => {
  const { data: response, isLoading } = useGetFaqSettings();
  const faqData = response as FaqSettingInterface | undefined;
  const { permission } = usePermissionStore();
  const { mutate: saveFaqs, isLoading: isSaving } = useSaveFaqSettings();
  const [showFaqToggle, setShowFaqToggle] = useState(false);
  const [adminFaqs, setAdminFaqs] = useState<Faq[]>([]);
  const [search, setSearch] = useState("");
  const [isEditorOpen, setIsEditorOpen] = useState(false);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [draft, setDraft] = useState<Faq>(EMPTY_FAQ);
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [deleteIndex, setDeleteIndex] = useState<number | null>(null);
  const [isBulkUploadOpen, setIsBulkUploadOpen] = useState(false);
  const [savedFaqSettings, setSavedFaqSettings] =
    useState<FaqSettingInterface | null>(null);

  useEffect(() => {
    if (faqData) {
      setAdminFaqs(faqData.faqSettings || []);
      setShowFaqToggle(faqData.isFaqEnabled);
      setSavedFaqSettings({
        isFaqEnabled: faqData.isFaqEnabled,
        faqSettings: faqData.faqSettings || [],
      });
    }
  }, [faqData]);

  const userCanEdit = useMemo(
    () =>
      determineUserAccessToResource(
        permission?.aclList || [],
        Resource.CompanySettings,
        Action.Edit
      ),
    [permission]
  );

  const filteredFaqs = useMemo(() => {
    const term = search.trim().toLowerCase();
    return adminFaqs
      .map((faq, index) => ({ faq, index }))
      .filter(
        ({ faq }) =>
          !term ||
          faq.question.toLowerCase().includes(term) ||
          faq.answer
            .replace(/<[^>]*>/g, "")
            .toLowerCase()
            .includes(term)
      );
  }, [adminFaqs, search]);

  const openCreate = () => {
    setEditingIndex(null);
    setDraft(EMPTY_FAQ);
    setIsEditorOpen(true);
  };

  const openEdit = (faq: Faq, index: number) => {
    setEditingIndex(index);
    setDraft({ ...faq });
    setIsEditorOpen(true);
  };

  const saveDraft = () => {
    const question = draft.question.trim();
    const answer = sanitizeFaqHtml(draft.answer).trim();
    if (!question || !answer || !answer.replace(/<[^>]*>/g, "").trim()) {
      toast.error("Please enter both a question and an answer.");
      return;
    }

    const faq = { question, answer };
    setAdminFaqs((current) =>
      editingIndex === null
        ? [faq, ...current]
        : current.map((item, index) => (index === editingIndex ? faq : item))
    );
    setIsEditorOpen(false);
  };

  const deleteFaq = () => {
    if (deleteIndex === null) return;
    setAdminFaqs((current) =>
      current.filter((_, itemIndex) => itemIndex !== deleteIndex)
    );
    setDeleteIndex(null);
  };

  const moveFaq = (from: number, to: number) => {
    if (from === to) return;
    setAdminFaqs((current) => {
      const next = [...current];
      const [faq] = next.splice(from, 1);
      next.splice(to, 0, faq);
      return next;
    });
  };

  const handleSaveAll = () => {
    const sanitizedFaqs = adminFaqs.map(sanitizeFaq);
    saveFaqs(
      { isFaqEnabled: showFaqToggle, faqSettings: sanitizedFaqs },
      {
        onSuccess: () => {
          setSavedFaqSettings({
            isFaqEnabled: showFaqToggle,
            faqSettings: sanitizedFaqs,
          });
          setAdminFaqs(sanitizedFaqs);
          toast.success("FAQ settings saved successfully.");
        },
        onError: () => {
          toast.error("Failed to save FAQ settings. Please try again.");
        },
      }
    );
  };

  const handleCancelChanges = () => {
    if (!savedFaqSettings) return;
    setAdminFaqs(savedFaqSettings.faqSettings);
    setShowFaqToggle(savedFaqSettings.isFaqEnabled);
    setSearch("");
  };

  const addImportedFaqs = (faqs: Faq[]) => {
    setAdminFaqs((current) => [...faqs.map(sanitizeFaq), ...current]);
    setIsBulkUploadOpen(false);
    toast.success(
      `${faqs.length} FAQ${
        faqs.length === 1 ? "" : "s"
      } added. Save changes to publish them.`
    );
  };

  return (
    <VStack className="gap-4">
      <BCHeader className="items-baseline" bcTitle="FAQ Settings" />
      <Box className="flex flex-col gap-5 rounded-lg px-8 py-2">
        <HStack className="items-center justify-between gap-4 py-3">
          <VStack className="w-1/2 gap-1">
            <h4 className="text-lg font-medium text-gray-dark">
              Frequently asked questions
            </h4>
            <p className="mt-1 text-sm text-gray-500">
              Manage the FAQs displayed in the employee portal.
            </p>
          </VStack>
          <VStack className="w-1/2 gap-1 inline-flex items-end justify-end">
            <HStack className="gap-2 inline-flex items-center justify-end">
              <span className="text-sm text-gray-500">
                Show FAQs in employee portal
              </span>
              <SwitchButton
                value={showFaqToggle}
                label=""
                onClick={() => {
                  if (!userCanEdit) return;
                  if (adminFaqs.length === 0 && !showFaqToggle) {
                    toast(
                      "Please add at least one FAQ before enabling the toggle.",
                      {
                        type: "warning",
                        autoClose: 2000,
                      }
                    );
                    return;
                  }
                  setShowFaqToggle(!showFaqToggle);
                }}
              />
            </HStack>
          </VStack>
        </HStack>
        <Box className="flex flex-col gap-4 rounded-lg  border-gray-300 bg-white p-4">
          {userCanEdit && (
            <HStack className="justify-end gap-3">
              <ButtonSecondary
                onClick={() => setIsBulkUploadOpen(true)}
                className="flex items-center gap-1.5"
              >
                <Icon icon="mdi:upload-outline" width={17} /> Import
              </ButtonSecondary>
              <ButtonPrimary
                onClick={openCreate}
                className="flex items-center gap-1.5"
              >
                <Icon icon="mdi:plus" width={17} /> Add FAQ
              </ButtonPrimary>
            </HStack>
          )}

          {isLoading ? (
            <div className="py-16 text-center text-sm text-gray-500">
              Loading FAQs…
            </div>
          ) : adminFaqs.length === 0 ? (
            <Box className="">
              <EmptyFaqPage />
            </Box>
          ) : (
            <VStack className="gap-4">
              <HStack className="items-center justify-between gap-4">
                <div className="relative w-full max-w-sm">
                  <Icon
                    icon="mdi:magnify"
                    className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500"
                    width={20}
                  />
                  <input
                    value={search}
                    onChange={(event) => setSearch(event.target.value)}
                    placeholder="Search FAQs"
                    className="w-full rounded-md border border-borderColor bg-white py-2.5 pl-10 pr-3 text-sm text-gray-dark outline-none focus:border-primary"
                  />
                </div>
              </HStack>
              <VStack className="gap-3 max-h-60 overflow-y-auto">
                {filteredFaqs.map(({ faq, index }) => (
                  <article
                    key={`${faq.question}-${index}`}
                    draggable={userCanEdit}
                    onDragStart={() => setDraggedIndex(index)}
                    onDragOver={(event) => event.preventDefault()}
                    onDrop={() => {
                      if (draggedIndex !== null) moveFaq(draggedIndex, index);
                      setDraggedIndex(null);
                    }}
                    className={`group flex items-start gap-5 rounded-lg border border-borderColor bg-white px-8 py-7 shadow-sm transition-shadow hover:shadow-md ${
                      draggedIndex === index ? "opacity-50" : ""
                    }`}
                  >
                    <div className="flex w-7 shrink-0 flex-col items-center gap-2 pt-1 text-gray-400">
                      {userCanEdit && (
                        <Icon
                          icon="mdi:drag-vertical"
                          width={22}
                          className="cursor-grab"
                        />
                      )}
                      <span className="text-sm font-semibold">
                        {String(index + 1).padStart(2, "0")}
                      </span>
                    </div>
                    <div className="min-w-0 flex-1">
                      <h3 className="text-xl font-semibold text-dark">
                        {faq.question}
                      </h3>
                      <div
                        className="faq-answer-preview mt-2 line-clamp-3 text-base leading-6 text-gray-600 [&_a]:font-medium [&_a]:text-primary [&_a]:underline [&_a]:underline-offset-2 [&_a:hover]:text-primaryHover [&_a:focus-visible]:rounded-sm [&_a:focus-visible]:outline [&_a:focus-visible]:outline-2 [&_a:focus-visible]:outline-offset-2 [&_a:focus-visible]:outline-primary"
                        dangerouslySetInnerHTML={{
                          __html: sanitizeFaqHtml(faq.answer),
                        }}
                      />
                    </div>
                    {userCanEdit && (
                      <HStack className="shrink-0 gap-1 opacity-0 transition-opacity group-hover:opacity-100 focus-within:opacity-100">
                        <IconButton
                          aria-label="Edit FAQ"
                          onClick={() => openEdit(faq, index)}
                          size="small"
                        >
                          <Icon
                            icon="mdi:pencil-outline"
                            width={20}
                            className="text-primary"
                          />
                        </IconButton>
                        <IconButton
                          aria-label="Delete FAQ"
                          onClick={() => setDeleteIndex(index)}
                          size="small"
                        >
                          <Icon
                            icon="mdi:delete-outline"
                            width={20}
                            className="text-red-500"
                          />
                        </IconButton>
                      </HStack>
                    )}
                  </article>
                ))}
                {filteredFaqs.length === 0 && (
                  <div className="py-12 text-center text-sm text-gray-500">
                    No FAQs match “{search}”.
                  </div>
                )}
              </VStack>
            </VStack>
          )}

          {userCanEdit && (
            <HStack className="justify-between gap-3 py-4">
              <ButtonSecondary onClick={handleCancelChanges}>
                Cancel
              </ButtonSecondary>
              <ButtonPrimary onClick={handleSaveAll} loading={isSaving}>
                Save changes
              </ButtonPrimary>
            </HStack>
          )}
        </Box>
      </Box>

      <FaqEditorDialog
        open={isEditorOpen}
        faq={draft}
        isEditing={editingIndex !== null}
        onChange={setDraft}
        onClose={() => setIsEditorOpen(false)}
        onSave={saveDraft}
      />
      <BulkFaqUploadDialog
        open={isBulkUploadOpen}
        onClose={() => setIsBulkUploadOpen(false)}
        onImport={addImportedFaqs}
        existingFaqs={adminFaqs}
      />
      <Dialog
        open={deleteIndex !== null}
        onClose={() => setDeleteIndex(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle className="px-6 pt-5 text-lg font-semibold text-dark">
          Delete FAQ?
        </DialogTitle>
        <DialogContent className="px-6 py-3 text-sm text-gray-600">
          This FAQ will be removed when you save your changes.
        </DialogContent>
        <DialogActions
          className="border-t border-gray-100 bg-gray-50 px-6 py-4"
          sx={{ justifyContent: "space-between" }}
        >
          <ButtonSecondary onClick={() => setDeleteIndex(null)}>
            Cancel
          </ButtonSecondary>
          <ButtonPrimary onClick={deleteFaq}>Delete FAQ</ButtonPrimary>
        </DialogActions>
      </Dialog>
    </VStack>
  );
};

function BulkFaqUploadDialog({
  open,
  onClose,
  onImport,
  existingFaqs,
}: {
  open: boolean;
  onClose: () => void;
  onImport: (faqs: Faq[]) => void;
  existingFaqs: Faq[];
}) {
  const [file, setFile] = useState<File | null>(null);
  const [result, setResult] = useState<FaqDocxParseResult | null>(null);
  const [isParsing, setIsParsing] = useState(false);
  const [activeDraftId, setActiveDraftId] = useState<string | null>(null);
  const [faqDrafts, setFaqDrafts] = useState<
    {
      id: string;
      questionBlockId: string;
      question: string;
      answerBlockIds: string[];
      answer: string;
    }[]
  >([]);
  const [editingDraftId, setEditingDraftId] = useState<string | null>(null);
  const [editingDraft, setEditingDraft] = useState<Faq>(EMPTY_FAQ);
  const [showUnmappedConfirm, setShowUnmappedConfirm] = useState(false);
  const [showResetConfirm, setShowResetConfirm] = useState(false);
  const [showDuplicateConfirm, setShowDuplicateConfirm] = useState(false);
  const [isSummaryVisible, setIsSummaryVisible] = useState(false);

  const selectFile = (selectedFile: File) => {
    if (selectedFile.size > 10 * 1024 * 1024) {
      toast.error("Upload a .docx file smaller than 10 MB.");
      return;
    }
    setFile(selectedFile);
    setResult(null);
    setFaqDrafts([]);
    setActiveDraftId(null);
    setIsSummaryVisible(false);
  };

  const parseFile = async () => {
    if (!file) {
      toast.error("Select a .docx file to continue.");
      return;
    }
    setIsParsing(true);
    try {
      const extension = file.name.split(".").pop()?.toLowerCase();
      const parsed =
        extension === "pdf"
          ? await parseFaqPdf(file)
          : await parseFaqDocx(file);
      setResult(parsed);
      setIsSummaryVisible(false);
    } catch (error: any) {
      toast.error(
        error?.response?.data?.message ||
          error?.message ||
          "Unable to process this DOCX file. Please try again."
      );
    } finally {
      setIsParsing(false);
    }
  };

  const close = () => {
    setFile(null);
    setResult(null);
    setFaqDrafts([]);
    setActiveDraftId(null);
    setEditingDraftId(null);
    setIsSummaryVisible(false);
    onClose();
  };

  const mappedBlockIds = new Set(
    faqDrafts.flatMap((draft) => [
      draft.questionBlockId,
      ...draft.answerBlockIds,
    ])
  );
  const activeDraft = faqDrafts.find((draft) => draft.id === activeDraftId);
  const unmappedBlocks =
    result?.blocks.filter((block) => !mappedBlockIds.has(block.id)) || [];

  const duplicateInfo = (draft: typeof faqDrafts[number]) => {
    const normalizedQuestion = normalizeQuestion(draft.question);
    const exactExisting = existingFaqs.find(
      (faq) => normalizeQuestion(faq.question) === normalizedQuestion
    );
    const exactDraft = faqDrafts.find(
      (item) =>
        item.id !== draft.id &&
        normalizeQuestion(item.question) === normalizedQuestion
    );
    if (exactExisting || exactDraft) {
      return {
        kind: "exact" as const,
        question: (exactExisting || exactDraft)?.question || "",
      };
    }
    const similarExisting = existingFaqs.find(
      (faq) => questionSimilarity(faq.question, draft.question) >= 0.7
    );
    const similarDraft = faqDrafts.find(
      (item) =>
        item.id !== draft.id &&
        questionSimilarity(item.question, draft.question) >= 0.7
    );
    if (similarExisting || similarDraft) {
      return {
        kind: "similar" as const,
        question: (similarExisting || similarDraft)?.question || "",
      };
    }
    return null;
  };

  const exactDuplicates = faqDrafts.filter(
    (draft) => duplicateInfo(draft)?.kind === "exact"
  );

  const markAsQuestion = (block: FaqDocxParseResult["blocks"][number]) => {
    const id = `faq-${Date.now()}`;
    setFaqDrafts((current) => [
      ...current,
      {
        id,
        questionBlockId: block.id,
        question: block.text,
        answerBlockIds: [],
        answer: "",
      },
    ]);
    setActiveDraftId(id);
  };

  const addToAnswer = (block: FaqDocxParseResult["blocks"][number]) => {
    if (!activeDraftId) {
      toast.info("Select an FAQ draft before adding an answer block.");
      return;
    }
    setFaqDrafts((current) =>
      current.map((draft) =>
        draft.id === activeDraftId
          ? {
              ...draft,
              answerBlockIds: [...draft.answerBlockIds, block.id],
              answer: `${draft.answer}${block.html}`,
            }
          : draft
      )
    );
  };

  const addNextBlock = (draftId = activeDraftId) => {
    const draftToUpdate = faqDrafts.find((draft) => draft.id === draftId);
    if (!draftToUpdate || !result) return;
    const sourceIds = [
      draftToUpdate.questionBlockId,
      ...draftToUpdate.answerBlockIds,
    ];
    const lastIndex = Math.max(
      ...sourceIds.map((id) =>
        result.blocks.findIndex((block) => block.id === id)
      )
    );
    const nextBlock = result.blocks
      .slice(lastIndex + 1)
      .find((block) => !mappedBlockIds.has(block.id));
    if (nextBlock) {
      setActiveDraftId(draftToUpdate.id);
      setFaqDrafts((current) =>
        current.map((draft) =>
          draft.id === draftToUpdate.id
            ? {
                ...draft,
                answerBlockIds: [...draft.answerBlockIds, nextBlock.id],
                answer: `${draft.answer}${nextBlock.html}`,
              }
            : draft
        )
      );
    } else toast.info("There are no remaining content blocks to add.");
  };

  const removeDraft = (draftId: string) => {
    setFaqDrafts((current) => current.filter((draft) => draft.id !== draftId));
    if (activeDraftId === draftId) setActiveDraftId(null);
  };

  const openDraftEditor = (draft: typeof faqDrafts[number]) => {
    setEditingDraftId(draft.id);
    setEditingDraft({ question: draft.question, answer: draft.answer });
  };

  const saveDraftEditor = () => {
    if (
      !editingDraft.question.trim() ||
      !editingDraft.answer.replace(/<[^>]*>/g, "").trim()
    ) {
      toast.error("Please enter both a question and an answer.");
      return;
    }
    setFaqDrafts((current) =>
      current.map((draft) =>
        draft.id === editingDraftId
          ? {
              ...draft,
              question: editingDraft.question.trim(),
              answer: sanitizeFaqHtml(editingDraft.answer),
            }
          : draft
      )
    );
    setEditingDraftId(null);
  };

  const finishConfirmation = () => {
    if (unmappedBlocks.length > 0) setShowUnmappedConfirm(true);
    else
      onImport(
        faqDrafts.map(({ question, answer }) =>
          sanitizeFaq({ question, answer })
        )
      );
  };

  const openSummary = () => {
    if (faqDrafts.length === 0) {
      toast.error("Map at least one question and answer before continuing.");
      return;
    }
    if (
      faqDrafts.some(
        (draft) =>
          !draft.question.trim() || !draft.answer.replace(/<[^>]*>/g, "").trim()
      )
    ) {
      toast.error("Complete every FAQ draft before continuing.");
      return;
    }
    setIsSummaryVisible(true);
  };

  const attemptConfirm = () => {
    if (exactDuplicates.length > 0) setShowDuplicateConfirm(true);
    else finishConfirmation();
  };

  const resetMappings = () => {
    setFaqDrafts([]);
    setActiveDraftId(null);
    setEditingDraftId(null);
    setIsSummaryVisible(false);
    setShowResetConfirm(false);
    toast.info("FAQ mappings have been reset.");
  };

  return (
    <Dialog
      open={open}
      onClose={close}
      maxWidth={false}
      fullWidth
      PaperProps={{ sx: { width: "96vw", maxWidth: "1440px" } }}
    >
      <DialogTitle className="flex items-center justify-between border-b border-gray-100 px-6 py-4 text-lg font-semibold text-dark">
        Bulk upload FAQs
        <IconButton aria-label="Close" onClick={close} size="small">
          <Icon icon="mdi:close" width={22} />
        </IconButton>
      </DialogTitle>
      <DialogContent className="p-6">
        <div className="mb-5 flex items-center gap-4 text-sm m-4">
          <span
            className={`font-medium ${
              result ? "text-green-600" : "text-primary"
            }`}
          >
            1. Upload
          </span>
          <span className="h-px flex-1 bg-gray-200" />
          <span
            className={`font-medium ${
              isSummaryVisible
                ? "text-green-600"
                : result
                ? "text-primary"
                : "text-gray-400"
            }`}
          >
            2. Map content
          </span>
          <span className="h-px flex-1 bg-gray-200" />
          <span
            className={`font-medium ${
              isSummaryVisible ? "text-primary" : "text-gray-400"
            }`}
          >
            3. Summary
          </span>
          <span className="h-px flex-1 bg-gray-200" />
          <span className="font-medium text-gray-400">4. Confirm</span>
        </div>
        {!result ? (
          <>
            <div className="mx-auto w-full max-w-2xl space-y-6">
              <div className="text-center">
                <h3 className="text-xl font-medium text-gray-900">
                  Upload file
                </h3>
                <p className="mt-1 text-gray-500">
                  Review and map the extracted content before adding FAQs.
                </p>
              </div>
              <FileDropzone
                onFileSelect={selectFile}
                onClear={() => {
                  setFile(null);
                  setResult(null);
                }}
                selectedFile={file || undefined}
                acceptedFileExtensions={[".docx", ".pdf"]}
                instructionText="DOCX or text-based PDF files, up to 10 MB"
              />
              <Instructions
                data={[
                  "DOCX is recommended because paragraphs, bullet lists, and tables can be preserved.",
                  "Text-based PDFs are supported, but are imported as text blocks and may lose formatting or table layout.",
                  "Scanned or image-only PDFs cannot be imported. Upload a DOCX version instead.",
                  "Legacy .doc files are not supported. Save the file as .docx before uploading.",
                  "Review and map every FAQ draft before confirming the import.",
                ]}
              />
            </div>
          </>
        ) : isSummaryVisible ? (
          <VStack className="mx-auto h-[60vh] w-full max-w-4xl gap-5 overflow-y-auto px-1 py-2">
            <VStack className="gap-1">
              <h3 className="text-xl font-medium text-gray-900">
                Mapped FAQ summary
              </h3>
              <p className="text-sm text-gray-500">
                Review the {faqDrafts.length} mapped FAQ
                {faqDrafts.length === 1 ? "" : "s"} before adding them to your
                FAQ settings.
              </p>
            </VStack>
            <div className="rounded-lg border border-borderColor bg-gray-50 px-4 py-3 text-sm text-gray-600">
              {unmappedBlocks.length} extracted content block
              {unmappedBlocks.length === 1 ? " is" : "s are"} not mapped to an
              FAQ.
            </div>
            <VStack className="gap-3">
              {faqDrafts.map((draft, index) => {
                const duplicate = duplicateInfo(draft);
                return (
                  <div
                    key={draft.id}
                    className="rounded-lg border border-borderColor bg-white p-4"
                  >
                    <HStack className="items-start justify-between gap-4">
                      <div>
                        <p className="text-xs font-semibold text-primary">
                          QUESTION {index + 1}
                        </p>
                        <p className="mt-1 font-medium text-dark">
                          {draft.question}
                        </p>
                      </div>
                      {duplicate && (
                        <span
                          className={`text-xs ${
                            duplicate.kind === "exact"
                              ? "text-red-600"
                              : "text-yellow-700"
                          }`}
                        >
                          {duplicate.kind === "exact"
                            ? "Duplicate question"
                            : "Possible duplicate"}
                        </span>
                      )}
                    </HStack>
                    <div className="mt-3 border-t border-borderColor pt-3">
                      <p className="text-xs font-semibold text-green-600">
                        ANSWER
                      </p>
                      <div
                        className="mt-1 text-sm text-gray-600"
                        dangerouslySetInnerHTML={{
                          __html: sanitizeFaqHtml(draft.answer),
                        }}
                      />
                    </div>
                  </div>
                );
              })}
            </VStack>
          </VStack>
        ) : (
          <div className="grid h-[60vh] min-h-0 grid-cols-2 gap-5 overflow-hidden">
            <VStack className="min-h-0 overflow-y-auto rounded-lg border border-borderColor bg-gray-50">
              <HStack className="items-center justify-between border-b border-borderColor bg-white px-4 py-3">
                <span className="font-semibold text-dark">
                  Extracted content
                </span>
                <span className="text-xs text-gray-500">
                  {unmappedBlocks.length} unmapped
                </span>
              </HStack>
              <VStack className="gap-3 p-3">
                {result.blocks.map((block) => {
                  const isQuestion = faqDrafts.some(
                    (draft) => draft.questionBlockId === block.id
                  );
                  const isAnswer = faqDrafts.some((draft) =>
                    draft.answerBlockIds.includes(block.id)
                  );
                  return (
                    <div
                      key={block.id}
                      className={`rounded-md border p-3 ${
                        isQuestion || isAnswer
                          ? "border-green-200 bg-green-50"
                          : "border-borderColor bg-white"
                      }`}
                    >
                      <div
                        className="max-h-36 overflow-hidden text-sm text-gray-700"
                        dangerouslySetInnerHTML={{
                          __html: sanitizeFaqHtml(block.html),
                        }}
                      />
                      <HStack className="mt-3 items-center gap-2">
                        {isQuestion || isAnswer ? (
                          <span className="text-xs font-medium text-green-600">
                            {isQuestion ? "Mapped question" : "Added to answer"}
                          </span>
                        ) : (
                          <>
                            <ButtonSecondary
                              onClick={() => markAsQuestion(block)}
                            >
                              Mark as question
                            </ButtonSecondary>
                            <ButtonSecondary
                              onClick={() => addToAnswer(block)}
                              disabled={!activeDraftId}
                            >
                              Add to answer
                            </ButtonSecondary>
                          </>
                        )}
                        {block.suggestedQuestion &&
                          !isQuestion &&
                          !isAnswer && (
                            <span className="text-xs text-primary">
                              Likely question
                            </span>
                          )}
                      </HStack>
                    </div>
                  );
                })}
              </VStack>
            </VStack>
            <VStack className="min-h-0 overflow-y-auto rounded-lg border border-borderColor bg-gray-50">
              <HStack className="items-center justify-between border-b border-borderColor bg-white px-4 py-3">
                <span className="font-semibold text-dark">FAQ drafts</span>
                <HStack className="items-center gap-3">
                  <span className="text-xs text-gray-500">
                    {faqDrafts.length} mapped
                  </span>
                  {faqDrafts.length > 0 && (
                    <button
                      type="button"
                      onClick={() => setShowResetConfirm(true)}
                      className="text-xs font-medium text-red-500 hover:text-red-600"
                    >
                      Reset mapping
                    </button>
                  )}
                </HStack>
              </HStack>
              <VStack className="gap-3 p-3">
                {faqDrafts.length === 0 && (
                  <p className="py-12 text-center text-sm text-gray-500">
                    Mark a content block as a question to start a draft.
                  </p>
                )}
                {faqDrafts.map((draft, index) =>
                  (() => {
                    const duplicate = duplicateInfo(draft);
                    return (
                      <div
                        key={draft.id}
                        role="button"
                        tabIndex={0}
                        onClick={() => setActiveDraftId(draft.id)}
                        onKeyDown={(event) => {
                          if (event.key === "Enter" || event.key === " ") {
                            setActiveDraftId(draft.id);
                          }
                        }}
                        className={`rounded-md border p-4 text-left ${
                          activeDraftId === draft.id
                            ? "border-primary bg-orange-50"
                            : "border-borderColor bg-white"
                        }`}
                      >
                        <HStack className="items-start justify-between gap-3">
                          <div>
                            <p className="text-xs font-semibold text-primary">
                              QUESTION {index + 1}
                            </p>
                            <p className="mt-1 font-medium text-dark">
                              {draft.question}
                            </p>
                          </div>
                          <IconButton
                            aria-label="Edit FAQ draft"
                            size="small"
                            onClick={(event) => {
                              event.stopPropagation();
                              openDraftEditor(draft);
                            }}
                          >
                            <Icon
                              icon="mdi:pencil-outline"
                              width={18}
                              className="text-primary"
                            />
                          </IconButton>
                        </HStack>
                        {duplicate && (
                          <p
                            className={`mt-2 text-xs ${
                              duplicate.kind === "exact"
                                ? "text-red-600"
                                : "text-yellow-700"
                            }`}
                          >
                            {duplicate.kind === "exact"
                              ? "Duplicate question"
                              : "Possible duplicate"}
                            : {duplicate.question}
                          </p>
                        )}
                        <div className="mt-3 border-t border-borderColor pt-3">
                          <p className="text-xs font-semibold text-green-600">
                            ANSWER
                          </p>
                          {draft.answer ? (
                            <div
                              className="mt-1 text-sm text-gray-600"
                              dangerouslySetInnerHTML={{
                                __html: sanitizeFaqHtml(draft.answer),
                              }}
                            />
                          ) : (
                            <p className="mt-1 text-sm text-gray-400">
                              Select content on the left to add an answer.
                            </p>
                          )}
                        </div>
                        <HStack className="mt-3 justify-between">
                          <ButtonSecondary
                            onClick={(event) => {
                              event.stopPropagation();
                              addNextBlock(draft.id);
                            }}
                          >
                            Add next block
                          </ButtonSecondary>
                          <button
                            type="button"
                            onClick={(event) => {
                              event.stopPropagation();
                              removeDraft(draft.id);
                            }}
                            className="text-xs font-medium text-red-500"
                          >
                            Remove
                          </button>
                        </HStack>
                      </div>
                    );
                  })()
                )}
              </VStack>
            </VStack>
          </div>
        )}
      </DialogContent>
      <DialogActions
        className="border-t border-gray-100 bg-gray-50 px-6 py-4"
        sx={{ justifyContent: "space-between", px: 3, py: 2 }}
      >
        {isSummaryVisible ? (
          <ButtonSecondary onClick={() => setIsSummaryVisible(false)}>
            Back to mapping
          </ButtonSecondary>
        ) : (
          <ButtonSecondary onClick={close}>Cancel</ButtonSecondary>
        )}
        {!result ? (
          <ButtonPrimary
            onClick={parseFile}
            loading={isParsing}
            disabled={!file}
          >
            Review FAQs
          </ButtonPrimary>
        ) : isSummaryVisible ? (
          <ButtonPrimary onClick={attemptConfirm}>Confirm import</ButtonPrimary>
        ) : (
          <ButtonPrimary onClick={openSummary}>
            Review mapped FAQs
          </ButtonPrimary>
        )}
      </DialogActions>
      <Dialog
        open={showUnmappedConfirm}
        onClose={() => setShowUnmappedConfirm(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Unmapped content</DialogTitle>
        <DialogContent>
          {unmappedBlocks.length} content block
          {unmappedBlocks.length === 1 ? " is" : "s are"} not included in an
          FAQ. Do you want to discard{" "}
          {unmappedBlocks.length === 1 ? "it" : "them"}?
        </DialogContent>
        <DialogActions sx={{ justifyContent: "space-between" }}>
          <ButtonSecondary
            onClick={() => {
              setShowUnmappedConfirm(false);
              setIsSummaryVisible(false);
            }}
          >
            Return to mapping
          </ButtonSecondary>
          <ButtonPrimary
            onClick={() =>
              onImport(
                faqDrafts.map(({ question, answer }) =>
                  sanitizeFaq({ question, answer })
                )
              )
            }
          >
            Discard and continue
          </ButtonPrimary>
        </DialogActions>
      </Dialog>
      <Dialog
        open={showDuplicateConfirm}
        onClose={() => setShowDuplicateConfirm(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Duplicate questions found</DialogTitle>
        <DialogContent>
          {exactDuplicates.length} mapped FAQ
          {exactDuplicates.length === 1 ? " matches" : "s match"} an existing or
          imported question. Review the highlighted drafts, or keep the
          duplicates and continue.
        </DialogContent>
        <DialogActions sx={{ justifyContent: "space-between" }}>
          <ButtonSecondary
            onClick={() => {
              setShowDuplicateConfirm(false);
              setIsSummaryVisible(false);
            }}
          >
            Return to mapping
          </ButtonSecondary>
          <ButtonPrimary
            onClick={() => {
              setShowDuplicateConfirm(false);
              finishConfirmation();
            }}
          >
            Keep duplicates
          </ButtonPrimary>
        </DialogActions>
      </Dialog>
      <Dialog
        open={showResetConfirm}
        onClose={() => setShowResetConfirm(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Reset all mappings?</DialogTitle>
        <DialogContent>
          All FAQ drafts and selected answer blocks will be cleared. Your
          uploaded document will remain available to map again.
        </DialogContent>
        <DialogActions sx={{ justifyContent: "space-between" }}>
          <ButtonSecondary onClick={() => setShowResetConfirm(false)}>
            Cancel
          </ButtonSecondary>
          <ButtonPrimary onClick={resetMappings}>Reset mapping</ButtonPrimary>
        </DialogActions>
      </Dialog>
      <FaqEditorDialog
        open={editingDraftId !== null}
        faq={editingDraft}
        isEditing
        onChange={setEditingDraft}
        onClose={() => setEditingDraftId(null)}
        onSave={saveDraftEditor}
      />
    </Dialog>
  );
}

function FaqEditorDialog({
  open,
  faq,
  isEditing,
  onChange,
  onClose,
  onSave,
}: {
  open: boolean;
  faq: Faq;
  isEditing: boolean;
  onChange: (faq: Faq) => void;
  onClose: () => void;
  onSave: () => void;
}) {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      disableEnforceFocus
    >
      <DialogTitle className="flex items-center justify-between border-b border-gray-100 px-6 py-4 text-lg font-semibold text-dark">
        {isEditing ? "Edit FAQ" : "Create FAQ"}
        <IconButton aria-label="Close" onClick={onClose} size="small">
          <Icon icon="mdi:close" width={22} />
        </IconButton>
      </DialogTitle>
      <DialogContent className="p-6">
        <InputWithLabel
          label="Question"
          value={faq.question}
          placeholder="e.g. What is an ESOP?"
          onChange={(event) =>
            onChange({ ...faq, question: event.target.value })
          }
        />
        <label className="mb-2 block text-sm font-medium text-dark">
          Answer
        </label>
        <TinyEditor
          apiKey="l8zomkw48zb8qhvwpahb2g5nodmnma6qjyz1acf2y14t53z6"
          value={faq.answer}
          onEditorChange={(answer) => onChange({ ...faq, answer })}
          init={{
            height: 380,
            menubar: false,
            plugins: "lists link table code autoresize",
            toolbar:
              "undo redo | blocks | bold italic underline | alignleft aligncenter alignright alignjustify | bullist numlist | link table | removeformat code",
            link_default_protocol: "https",
            link_target_list: [
              { title: "Open in the same tab", value: "" },
              { title: "Open in a new tab", value: "_blank" },
            ],
            content_style:
              "body { font-family:Helvetica,Arial,sans-serif; font-size:14px; line-height:1.6; padding:12px; } a { color:#E85936; font-weight:500; text-decoration:underline; text-underline-offset:2px; } a:hover { color:#D15031; } table { border-collapse: collapse; width: 100%; } td, th { border: 1px solid #d1d5db; padding: 6px; }",
            resize: false,
          }}
        />
      </DialogContent>
      <DialogActions
        className="border-t border-gray-100 bg-gray-50 px-6 py-4"
        sx={{ justifyContent: "space-between" }}
      >
        <ButtonSecondary onClick={onClose}>Cancel</ButtonSecondary>
        <ButtonPrimary onClick={onSave}>
          {isEditing ? "Save changes" : "Create FAQ"}
        </ButtonPrimary>
      </DialogActions>
    </Dialog>
  );
}

function EmptyFaqPage() {
  return (
    <VStack className="items-center">
      <div className="mx-auto">
        <img
          src="https://equion-dev.s3.us-west-2.amazonaws.com/assets/emptyTable.png"
          alt="No FAQs Found"
          height="300"
          width="300"
        />
      </div>
      <p>No FAQs found!! Start by adding some</p>
    </VStack>
  );
}

export default FaqComponent;
